import { Injectable } from '@angular/core';
import { DomainService as DomainSDKService, ListAllDomainsResponse, SenderType } from '@vendasta/email';
// @ts-expect-error Angular 21 doesn't like imports past the base package
import { GetPendingEmailDomainResponse } from '@vendasta/email/lib';
import { BehaviorSubject, Observable, EMPTY } from 'rxjs';
import { shareReplay, skipWhile, take, tap, catchError } from 'rxjs/operators';

@Injectable()
export class DomainCacheService {
  private domainCache = new Map<string, BehaviorSubject<ListAllDomainsResponse>>();
  constructor(private readonly sdk: DomainSDKService) {}

  listAllDomains(senderType: SenderType, senderID: string): Observable<ListAllDomainsResponse> {
    const cacheKey = this.buildCacheKey(senderType, senderID);
    let cachedResponse = this.domainCache.get(cacheKey);

    if (!cachedResponse) {
      cachedResponse = new BehaviorSubject<ListAllDomainsResponse>(null);
      this.domainCache.set(cacheKey, cachedResponse);

      this.sdk
        .listAllDomains(senderType, senderID)
        .pipe(
          shareReplay(1),
          catchError(() => EMPTY),
        )
        .subscribe((response) => cachedResponse.next(response));
    }

    return cachedResponse.asObservable().pipe(skipWhile((response) => !response));
  }

  private refreshDomains(senderType: SenderType, senderID: string): void {
    const cacheKey = this.buildCacheKey(senderType, senderID);
    let cachedResponse = this.domainCache.get(cacheKey);

    if (!cachedResponse) {
      cachedResponse = new BehaviorSubject<ListAllDomainsResponse>(null);
      this.domainCache.set(cacheKey, cachedResponse);
    }

    this.sdk
      .listAllDomains(senderType, senderID)
      .pipe(
        shareReplay(1),
        take(1),
        catchError(() => EMPTY),
      )
      .subscribe((response) => cachedResponse.next(response));
  }

  createPendingDomain(
    senderType: SenderType,
    senderID: string,
    domain: string,
    _options?: { domainType?: number },
  ): Observable<GetPendingEmailDomainResponse> {
    const d = this.sdk
      .createPendingDomain(senderType, senderID, domain, _options?.domainType)
      .pipe(tap(() => this.refreshDomains(senderType, senderID)));
    return d;
  }

  deletePendingDomain(senderType: SenderType, senderID: string, domain: string): Observable<null> {
    return this.sdk.deletePendingDomain(senderType, senderID, domain).pipe(
      tap(() => this.refreshDomains(senderType, senderID)),
      catchError(() => EMPTY),
    );
  }

  private buildCacheKey(senderType: SenderType, senderID: string): string {
    return senderType.toString() + ':' + senderID;
  }
}
